import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { A11y, Navigation, Pagination } from "swiper/core";
import { arrayOf, node, shape, string, bool, number } from "prop-types";
import { nextIcon, prevIcon } from "../../common/icons.jsx";
import { useEffect, useState } from "react";

import FadeIn from "react-fade-in";
import RichText from "../RichText/RichText.jsx";
//import useMatchMedia from "../../common/useMatchMedia.js";

import CallToAction from "../CallToAction/CallToAction.jsx";

const propTypes = {
  darkMode: bool,
  variantType: number,
  cta: shape({ text: string, href: string }),
  title: string,
  description: node,
  slides: arrayOf(
    shape({
      image: shape({
        mobile1X: string.isRequired,
        mobile2X: string.isRequired,
        desktop1X: string.isRequired,
        desktop2X: string.isRequired,
        src: string,
        alt: string,
        caption: node,
      }),
      video: shape({
        images: shape({
          mobile1X: string.isRequired,
          mobile2X: string.isRequired,
          desktop1X: string.isRequired,
          desktop2X: string.isRequired,
          alt: string,
        }),
        link: string.isRequired,
      }),
    }),
  ),
};

const Carousel = (props) => {
  const { darkMode, variantType, cta, title, description, slides } = props;

  const [renderSwipers, setRenderSwipers] = useState(false);

  const [activeIndex, setActiveIndex] = useState(0);

  //matches bp-large-min in breakpoints.scss
  //const isDesktop = useMatchMedia("(min-width: 1060px)");

  useEffect(() => {
    //required to let text load and prevent swiper init errors
    setRenderSwipers(true);
  }, []);

  SwiperCore.use([A11y, Pagination, Navigation]);

  return (
    <section
      className={`slide-show-carousel ${
        darkMode && "slide-show-carousel--dark"
      }`}
      data-variant={variantType}
    >
      <div className="container container--100">
        {(cta || title || description) && (
          <div className="slide-show-carousel__header">
            {cta && (
              <div className="slide-show-carousel__cta">
                <CallToAction href={cta.href}>{cta.text}</CallToAction>
              </div>
            )}

            {title && (
              <div className="component__header">
                <RichText>
                  <h2>{title}</h2>
                </RichText>
              </div>
            )}

            {description && (
              <div className="component__description">
                <RichText>
                  <div dangerouslySetInnerHTML={{ __html: description }}></div>
                </RichText>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="slide-show-carousel__wrapper container--bleed">
        {renderSwipers && (
          <Swiper
            onSlideChange={(s) => setActiveIndex(s.realIndex)}
            freeMode={false}
            slidesPerView={1}
            a11y
            observer={true}
            observeParents={true}
            pagination={{ clickable: true }}
            spaceBetween={30}
            loop={true}
            navigation={{
              prevEl: ".js-carousel-prev",
              nextEl: ".js-carousel-next",
            }}
            breakpoints={{
              1060: {
                freeMode: true,
                slidesPerView: 1.5,
              },
              1366: {
                slidesPerView: 1.7,
              },
            }}
          >
            {slides.map((slide, i) => (
              <SwiperSlide key={i}>
                <div
                  className={`slide-show-carousel__slide ${
                    variantType === 2 &&
                    "slide-show-carousel__slide--variant-two"
                  } ${slide.video && "slide-show-carousel__slide--video"}`}
                >
                  {slide.video && (
                    <>
                      <div className="slide-show-carousel__slide-image">
                        {variantType === 2 ? (
                          <img
                            srcSet={`
                          ${slide.video.images.mobile1X} 250w,
                          ${slide.video.images.mobile2X} 500w,
                          ${slide.video.images.desktop1X} 620w,
                          ${slide.video.images.desktop2X} 930w
                          `}
                            sizes="
                            (min-width: 1115px) 620px, 
                            (min-width: 1060px) calc(57vw - 30px), 
                            (min-width: 690px) 620px, 
                            calc(100vw - 70px)
                          "
                            alt={
                              slide.video.images.alt
                                ? slide.video.images.alt
                                : ""
                            }
                          />
                        ) : (
                          <picture>
                            <source
                              srcSet={`${slide.video.images.desktop1X} 1x, ${slide.video.images.desktop2X} 2x`}
                              media="(min-width: 1060px)"
                            />
                            <img
                              alt={
                                slide.video.images.alt
                                  ? slide.video.images.alt
                                  : ""
                              }
                              src={slide.video.images.mobile1X}
                              srcSet={`${slide.video.images.mobile1X} 414w, ${slide.video.images.mobile2X} 640w`}
                            />
                          </picture>
                        )}
                        <a
                          className="play-video"
                          rel="noopener noreferrer"
                          href={slide.video.link}
                          target="_blank"
                        >
                          <span className="visually-hidden">play video</span>
                        </a>
                      </div>
                      {slide.video.images.caption &&
                        activeIndex === i &&
                        variantType === 2 && (
                          <div className="slide-show-carousel__slide-caption">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: slide.video.images.caption,
                              }}
                            ></div>
                          </div>
                        )}
                    </>
                  )}
                  {slide.image && (
                    <>
                      <div className="slide-show-carousel__slide-image">
                        {variantType === 2 ? (
                          <img
                            srcSet={`
                            ${slide.image.mobile1X} 250w,
                            ${slide.image.mobile2X} 500w,
                            ${slide.image.desktop1X} 620w,
                            ${slide.image.desktop2X} 930w
                            `}
                            sizes="
                              (min-width: 1115px) 620px, 
                              (min-width: 1060px) calc(57vw - 30px), 
                              (min-width: 690px) 620px, 
                              calc(100vw - 70px)
                            "
                            alt=""
                          />
                        ) : (
                          <picture>
                            <source
                              srcSet={`${slide.image.desktop1X} 1x, ${slide.image.desktop2X} 2x`}
                              media="(min-width: 1060px)"
                            />
                            <img
                              alt={slide.image.alt ? slide.image.alt : ""}
                              src={slide.image.mobile1X}
                              srcSet={`${slide.image.mobile1X} 414w, ${slide.image.mobile2X} 640w`}
                            />
                          </picture>
                        )}
                      </div>
                      {slide.image.caption && activeIndex === i && (
                        <>
                          {variantType === 2 ? (
                            <div className="slide-show-carousel__slide-caption">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: slide.image.caption,
                                }}
                              ></div>
                            </div>
                          ) : (
                            <FadeIn>
                              <div className="slide-show-carousel__slide-caption">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: slide.image.caption,
                                  }}
                                ></div>
                              </div>
                            </FadeIn>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        <div className="slide-show-carousel__navigation">
          <button
            aria-label="previous slide"
            className="js-carousel-prev slide-show-carousel__prev"
          >
            {prevIcon}
          </button>
          <button
            aria-label="next slide"
            className="js-carousel-next slide-show-carousel__next"
          >
            {nextIcon}
          </button>
        </div>
      </div>
      <div className="slide-show-carousel__border-bottom container container--100">
        <div></div>
      </div>
    </section>
  );
};

// Specifies the default values for props:
Carousel.defaultProps = {
  darkMode: false,
  variantType: 1,
};

Carousel.propTypes = propTypes;
export default Carousel;
